import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { listBooks, type Book } from "../../api/pqx";
import { useMe } from "../../components/MeProvider";
import { useAsync } from "../../lib/useAsync";
import { JsonUpload } from "../UploadScreen";
import {
  BOOK_STATE_CLASSES,
  RUNNING_STATES,
  bookStateLabel,
  checkPass,
  queueLabel,
} from "./bookStates";
import { PqxUpload } from "./PqxUpload";
import {
  CancelBookButton,
  RemoveBookButton,
  canCancelBook,
  canRemoveBook,
} from "./RemoveBookButton";

/** The same cadence as the book page, so both show the same progress. */
const POLL_MS = 5000;

type Tab = "mine" | "all" | "upload" | "json";

const TABS: [Tab, string][] = [
  ["mine", "My books"],
  ["all", "All books"],
  ["upload", "Upload book"],
  ["json", "Import JSON"],
];

/**
 * One place for getting questions in: scanned books through the extraction
 * engine and, for files that are already structured, a JSON import.
 *
 * Opens on "Upload book" for someone with no books yet, and on "My books"
 * otherwise. The tab is in the URL (`?tab=`) so it survives a reload.
 */
/**
 * The status chip's text: "Extracting questions 24%", "Queued · 2nd in line",
 * or, once the pages are done, the pass the engine is in: "Checking answers 37/308".
 */
function listProgress(b: Book): string {
  const label = bookStateLabel(b.state);
  if (b.state === "queued") {
    const place = queueLabel(b.queue_position);
    return place ? `${label} · ${place}` : label;
  }
  const pass = b.state === "checking_answers" ? checkPass(b.progress) : null;
  if (pass) return `${pass.step} ${pass.done}/${pass.total}`;
  if (
    b.progress &&
    RUNNING_STATES.has(b.state) &&
    b.state !== "checking_answers" &&
    b.progress.percent > 0
  )
    return `${label} ${b.progress.percent}%`;
  return label;
}

export function BooksScreen() {
  const { me } = useMe();
  const [params, setParams] = useSearchParams();
  const { data, loading, error } = useAsync(() => listBooks(), []);
  const [chosen, setChosen] = useState<Tab | null>(null);
  // Newer lists from polling. Kept apart from `data` so a poll never shows
  // "Loading…" or moves the tab; a failed poll keeps the last good list.
  const [fresh, setFresh] = useState<Book[] | null>(null);
  const [polls, setPolls] = useState(0);

  const all = fresh ?? data?.items ?? [];
  const running = all.some((b) => RUNNING_STATES.has(b.state));

  // One timer per poll; `polls` re-arms it after each attempt, failed or not.
  useEffect(() => {
    if (!running) return;
    let active = true;
    const timer = setTimeout(() => {
      listBooks()
        .then((page) => active && setFresh(page.items))
        .catch(() => undefined)
        .finally(() => active && setPolls((n) => n + 1));
    }, POLL_MS);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [running, polls]);

  const mine = all.filter((b) => b.reviewer === me?.id);

  // After a removal: fetch the list again in place, without "Loading…".
  const reload = () => {
    listBooks()
      .then((page) => setFresh(page.items))
      .catch(() => undefined);
  };

  const fromUrl = params.get("tab") as Tab | null;
  const tab: Tab =
    chosen ??
    (fromUrl && TABS.some(([t]) => t === fromUrl)
      ? fromUrl
      : loading || mine.length
        ? "mine"
        : "upload");

  const pick = (next: Tab) => {
    setChosen(next);
    setParams(next === "mine" ? {} : { tab: next }, { replace: true });
  };

  const books: Book[] = tab === "mine" ? mine : all;

  return (
    <div className="mx-auto max-w-5xl">
      <h1 className="text-[18px] font-bold lg:text-[22px]">Books</h1>
      <p className="mt-1 text-[13px] text-text-muted">
        Upload a past-questions book and the engine extracts it into the question bank for review.
      </p>

      <div
        role="tablist"
        aria-label="Books"
        className="mt-4 flex gap-1 overflow-x-auto border-b border-border"
      >
        {TABS.map(([key, label]) => (
          <button
            key={key}
            role="tab"
            type="button"
            aria-selected={tab === key}
            onClick={() => pick(key)}
            className={`-mb-px shrink-0 border-b-2 px-3 py-2 text-[13px] font-medium ${
              tab === key
                ? "border-accent text-text"
                : "border-transparent text-text-muted hover:text-text"
            }`}
          >
            {label}
            {key === "mine" && mine.length ? (
              <span className="ml-1.5 rounded-full bg-surface-high px-1.5 text-[11px] tabular-nums">
                {mine.length}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      <div className="mt-5">
        {tab === "upload" ? <PqxUpload /> : null}
        {tab === "json" ? <JsonUpload /> : null}
        {tab === "mine" || tab === "all" ? (
          <>
            {loading ? <p className="text-text-muted">Loading books…</p> : null}
            {error ? <p className="text-negative">{error.message}</p> : null}
            {!loading && !error && books.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border p-6 text-center text-[13px] text-text-muted">
                No books yet.{" "}
                <button
                  type="button"
                  onClick={() => pick("upload")}
                  className="font-semibold text-accent-light"
                >
                  Upload a book
                </button>{" "}
                to start.
              </div>
            ) : null}
            <ul className="flex flex-col gap-2">
              {books.map((b) => (
                <li key={b.book_id} className="flex items-stretch gap-2">
                  <Link
                    to={`/books/${b.book_id}`}
                    className="flex min-w-0 flex-1 flex-col gap-1 rounded-lg border border-border bg-surface px-4 py-3 hover:border-accent sm:flex-row sm:items-center sm:justify-between"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-[14px] font-semibold">{b.filename}</span>
                      <span className="text-[12px] text-text-muted">
                        {[b.subject?.name, b.exam_type].filter(Boolean).join(" · ")}
                        {b.imported ? ` · ${b.imported} questions` : ""}
                      </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-3 text-[12px] text-text-muted">
                      <span>{b.reviewer_name ?? "—"}</span>
                      {b.verdict === "FAIL" ? (
                        <span
                          title="The engine's quality check failed this book: review it closely"
                          className="rounded-full bg-warning/15 px-2.5 py-0.5 text-warning"
                        >
                          Quality check failed
                        </span>
                      ) : null}
                      <span
                        className={`rounded-full px-2.5 py-0.5 ${BOOK_STATE_CLASSES[b.state] ?? "bg-surface-high text-text"}`}
                      >
                        {listProgress(b)}
                      </span>
                    </span>
                  </Link>
                  {canCancelBook(b, me) ? (
                    <span className="flex items-center">
                      <CancelBookButton book={b} compact onCancelled={reload} />
                    </span>
                  ) : null}
                  {canRemoveBook(b, me) ? (
                    <span className="flex items-center">
                      <RemoveBookButton book={b} compact onRemoved={reload} />
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </div>
    </div>
  );
}
